// The SELECT tool: trigger on a selected voxel applies the current
// select-action (MOVE drag, or DELETE in VR's erase select-action); trigger
// anywhere else (empty space, unselected voxel, surface) starts a marquee so
// the user can build a new selection. Owns the marquee_select commit. A move
// drag it starts is a move_selection interaction — committed by the MOVE tool,
// which owns that kind (see app/tool-registry).

import type * as THREE from "three";
import { uiState } from "../ui/state";
import { interaction } from "../interaction/state";
import { type PickTarget } from "../interaction/pick";
import { type MirrorConfig } from "../interaction/mirror";
import { startMoveSelection } from "../interaction/handlers/move";
import { commitMarquee, startMarquee } from "../interaction/handlers/marquee";
import { SELECT_LASER_LENGTH, laserTipDistance } from "../view/controller-ray";
import { grid } from "../core/edit-core/grid";
import {
  deleteSelection,
  selFloatValid,
  selection,
} from "../core/edit-core/selection";
import { type ClipboardApi } from "../core/edit-core/clipboard";
import { type ToolRegistry } from "../app/tool-registry";

export interface SelectToolDeps {
  tools: ToolRegistry;
  pickFor: (i: number) => PickTarget | null; // the controller's last-frame pick
  gridGroup: THREE.Object3D;
  mirrorConfigNow: () => MirrorConfig | null;
  screenDriven: () => boolean; // desktop mouse (true) vs VR laser (false)
  clipboard: ClipboardApi; // adoptSelFloat at grab time
  headCamera: () => THREE.Camera; // XR camera while presenting, else desktop
  // Controller→grab-bar distance: the empty-space cursor's depth, used to anchor
  // the marquee window when the laser isn't on anything.
  emptySpaceHitDist: (i: number) => number;
  marqueeRect: { hide(): void }; // the marquee preview overlay (main owns it)
}

export function installSelectTool(deps: SelectToolDeps): void {
  deps.tools.register({
    id: "select",
    claims: () => uiState.topMode === "select",
    start: (i) => {
      const pick = deps.pickFor(i);
      const onSelected =
        pick?.kind === "voxel" && selection.has(pick.ix, pick.iy, pick.iz);
      if (onSelected) {
        // Desktop has no erase-on-click subtool (deletion is Backspace / the
        // Delete-selected button), so a selected-voxel click always moves there.
        // VR still honours its select-action dial.
        if (uiState.selectAction === "move" || deps.screenDriven()) {
          const grabsSelFloat = selFloatValid();
          if (
            startMoveSelection(
              pick,
              i,
              grid,
              deps.gridGroup,
              selection,
              deps.screenDriven(),
              deps.mirrorConfigNow(),
            )
          ) {
            deps.clipboard.adoptSelFloat(grabsSelFloat);
          }
        } else {
          deleteSelection();
        }
        return;
      }
      // Anchor the marquee window at the VISIBLE tip of the laser stub, not
      // the raw hit distance (the laser is a short fixed-length stub, so a
      // far pick would otherwise place the window metres past the tip). Use
      // the pick distance when the laser is on something; otherwise the
      // controller→grab-bar distance (the empty-space cursor's depth).
      const hitDist = pick ? pick.distance : deps.emptySpaceHitDist(i);
      startMarquee(i, deps.headCamera(), laserTipDistance(hitDist, SELECT_LASER_LENGTH));
    },
    kinds: ["marquee_select"],
    commit: (i) => {
      if (interaction.kind !== "marquee_select" || interaction.controllerIndex !== i) {
        return;
      }
      commitMarquee(grid, deps.gridGroup, selection);
      deps.marqueeRect.hide();
    },
  });
}
